/**
 * Product Gallery Nav Dots
 * --------------------------------------------------------------------------
 * Adds Oura-style arrow + dot navigation to Dawn's product media gallery on
 * desktop (>=768px), for the "Thumbnail carousel" desktop layout.
 *
 * GALLERY STRUCTURE (confirmed for this store):
 * - Slide 0 in the DOM is always the currently active variant's image
 *   (carries both `is-active` and `product__media-item--variant`).
 * - Exactly 5 other slides are shared across all variants (no
 *   `product__media-item--variant` class at all).
 * - All remaining slides belong to OTHER, non-selected variants and
 *   must never be counted, shown as dots, or navigated to.
 * So the navigable set for any given variant is always exactly:
 *   [the active slide] + [the 5 slides without --variant] = 6 total.
 *
 * NAVIGATION MECHANISM:
 * This layout does not scroll horizontally — only the active slide is
 * ever rendered visible (display:block), all others are display:none.
 * Switching slides happens by calling the <media-gallery> element's
 * public setActiveMedia(mediaId, prepend) method, the same method
 * Dawn's own thumbnail buttons call internally.
 *
 * IMPORTANT: Dawn's own SliderComponent already attaches click listeners
 * to the .slider-button--prev/--next buttons (for scroll-based layouts,
 * where they'd be relevant). On this layout those listeners are
 * effectively dead weight, but having TWO sets of listeners (Dawn's +
 * ours) on the same buttons risks side effects / state drift. To avoid
 * that entirely, we clone-and-replace the buttons before attaching our
 * own listeners, which strips any previously-attached listeners and
 * guarantees our code is the only thing driving them.
 * --------------------------------------------------------------------------
 */

(function () {
  'use strict';

  function initGalleryNav(galleryEl) {
    if (galleryEl.dataset.navDotsInit === 'true') return;

    var sliderComponent = galleryEl.querySelector('slider-component');
    if (!sliderComponent) return;

    var track = sliderComponent.querySelector('ul[id^="Slider-Gallery-"]');
    var buttonsWrap = sliderComponent.querySelector('.slider-buttons');
    if (!track || !buttonsWrap) return;

    function isSharedSlide(el) {
      return !el.classList.contains('product__media-item--variant');
    }

    function isActiveSlide(el) {
      return el.classList.contains('is-active');
    }

    // Build the navigable set: active slide first, then the 5 shared
    // slides, in DOM order. De-duplicated in case the active slide is
    // ever also a "shared" one (shouldn't happen per the confirmed
    // structure, but guards against it safely).
    function getNavigableSlides() {
      var all = Array.prototype.slice.call(track.querySelectorAll('li[id^="Slide-"]'));
      var active = all.filter(isActiveSlide);
      var shared = all.filter(function (el) {
        return isSharedSlide(el) && !isActiveSlide(el);
      });
      return active.concat(shared);
    }

    var slides = getNavigableSlides();
    if (slides.length === 0) return;

    if (slides.length === 1) {
      buttonsWrap.setAttribute('data-single-slide', 'true');
      return;
    }

    galleryEl.dataset.navDotsInit = 'true';

    var mediaIds = slides.map(function (slide) {
      return slide.getAttribute('data-media-id');
    });

    var activeIndex = 0; // slide 0 in our array is always the active one at init

    function goToIndex(index) {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;
      activeIndex = index;

      if (typeof galleryEl.setActiveMedia === 'function') {
        galleryEl.setActiveMedia(mediaIds[index], false);
      } else {
        var thumb = galleryEl.querySelector(
          '.thumbnail-list__item[data-target="' + mediaIds[index] + '"] button'
        );
        if (thumb) thumb.click();
      }

      setActiveDot(index);
    }

    // --- Build dot container ---
    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'gallery-dots';
    dotsWrap.setAttribute('role', 'tablist');
    dotsWrap.setAttribute('aria-label', 'Select image');

    var dots = slides.map(function (slide, index) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'gallery-dots__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Show image ' + (index + 1) + ' of ' + slides.length);
      if (index === 0) dot.classList.add('is-active');

      dot.addEventListener('click', function () {
        goToIndex(index);
      });

      dotsWrap.appendChild(dot);
      return dot;
    });

    function setActiveDot(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    var counter = buttonsWrap.querySelector('.slider-counter');
    if (counter) {
      buttonsWrap.insertBefore(dotsWrap, counter);
    } else {
      var existingNext = buttonsWrap.querySelector('.slider-button--next');
      buttonsWrap.insertBefore(dotsWrap, existingNext || null);
    }

    // --- Strip Dawn's existing listeners by cloning the buttons, then
    // attach our own. This guarantees we're the only code reacting to
    // these clicks. ---
    var oldPrev = buttonsWrap.querySelector('.slider-button--prev');
    var oldNext = buttonsWrap.querySelector('.slider-button--next');

    var prevBtn = oldPrev ? oldPrev.cloneNode(true) : null;
    var nextBtn = oldNext ? oldNext.cloneNode(true) : null;

    if (oldPrev && prevBtn) oldPrev.replaceWith(prevBtn);
    if (oldNext && nextBtn) oldNext.replaceWith(nextBtn);

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goToIndex(activeIndex - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goToIndex(activeIndex + 1);
      });
    }

    // --- Keep dots in sync if a thumbnail is clicked directly. Find the
    // matching index in our slides array by media id rather than
    // re-deriving from is-active, so it stays consistent with our own
    // ordering even if the thumbnail click changes which variant's
    // slide is active. ---
    var thumbnailButtons = galleryEl.querySelectorAll('.thumbnail-list .thumbnail');
    thumbnailButtons.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        setTimeout(function () {
          var nowActive = track.querySelector('li[id^="Slide-"].is-active');
          if (!nowActive) return;
          var nowActiveId = nowActive.getAttribute('data-media-id');
          var idx = mediaIds.indexOf(nowActiveId);
          if (idx !== -1) {
            activeIndex = idx;
            setActiveDot(idx);
          } else {
            // Thumbnail click landed on a slide outside our navigable
            // set (e.g. switched to a different variant) — rebuild.
            rebuild();
          }
        }, 50);
      });
    });

    // --- Rebuild everything if the variant changes the active slide to
    // one outside our current navigable set (i.e. a real variant swap,
    // not just navigating among the current 6). ---
    function rebuild() {
      dotsWrap.remove();
      buttonsWrap.removeAttribute('data-single-slide');
      galleryEl.dataset.navDotsInit = 'false';
      observer.disconnect();
      initGalleryNav(galleryEl);
    }

    var allSlides = Array.prototype.slice.call(track.querySelectorAll('li[id^="Slide-"]'));
    var rebuildTimeout;
    var observer = new MutationObserver(function (mutations) {
      // Only react to is-active class changes, and only if the new
      // active slide isn't one we already know about (real variant
      // change), to avoid rebuilding during our own navigation.
      var relevant = mutations.some(function (m) {
        return m.target.classList && m.target.classList.contains('is-active') &&
          mediaIds.indexOf(m.target.getAttribute('data-media-id')) === -1;
      });
      if (!relevant) return;

      clearTimeout(rebuildTimeout);
      rebuildTimeout = setTimeout(rebuild, 100);
    });
    allSlides.forEach(function (slide) {
      observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(initGalleryNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();